import get from 'lodash/get';
import {AttrsSchema, Component} from 'panel';

import {currentProjectStore, ProjectDetails, projectDetailsStore} from 'common/report/stores/project-details';
import {supportInfoStore} from 'common/report/stores/support-info';
import {userDetailsStore} from 'common/report/stores/user-details';
import {featuresStore} from 'common/report/stores/features';
import {
  closestReportLink,
  debug,
  excludeDemos,
  getProjectId,
  getReportUrl,
  getWorkspaceId,
  isPersistableReport,
} from 'common/report/util';
import {spaNavManager} from 'common/report/util/spa-nav-manager';
import {createOrgProjects} from 'common/report/queries/app-api/legacy/orgs-settings-client';
import {WorkspaceObj, workspacesClient} from 'common/report/queries/app-api/workspaces-client';
import {viewedReportFlagManager} from 'common/report/queries/app-api/user-flags';
import {events, properties, track} from 'common/report/util/settings-tracking';
import {makePathFromUrlState, parseStateFromUrl} from 'common/report/util/state-from-url';
import {capitalize, nameToInitials, pluralize, truncateMiddle} from 'common/util/string';
import {useEventListener} from 'common/util/component-helpers';
import {getTourState, isChameleonReady, showTour, TourId, TourState} from 'common/util/chameleon';
import {getCookie, safeSetCookie} from 'common/util/cookie';
import {keyEventSwitcher} from 'common/util/dom';
import {stickyElemHooks} from 'common/util/dom-sticky-stack';
import {FestiveMode, getFestiveMode, setFestiveMode} from 'common/util/festive-mode';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {
  ApiFailureDescription,
  sortWorkspaces,
  UISourceObject,
  workspaceApiFailure,
  workspaceCreateEvent,
  workspacesTrackingPrefix,
} from 'common/util/workspaces';
import {promptManageWorkspaceModal} from 'common/widgets/create-entity-modal';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {DEFAULT_SEARCH_QUERY_STRING} from 'common/report/util/search';
import {
  ContentDiscoveryType,
  getSearchCommonTrackingProperties,
  SearchPhaseVersion,
  SearchSource,
  SearchTrackedProperty,
} from 'common/report/util/search/tracking';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {useNewMetricsMode} from 'common/widgets/query-builder/util';
import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {reporter} from 'common/report/error-reporting';
import {purgeFetchCache} from 'common/report/queries/utils/fetch-cache';
import {Visibility} from 'common/components/base/stackable';
import {openDemoProjectSelectorModal} from 'common/widgets/demo-selector-modal';
import {getPlatformApps, PlatformApp} from 'common/report/queries/app-api/platform-apps-client';
import {
  FeatureGate,
  INTERNAL_PROJECTS,
  ProjectFeatureFlag,
  ProjectType,
  UserFeatureFlag,
} from 'common/report/constants';
import {
  activatePersistedZendeskChat,
  activateZendeskChat,
  activateZendeskHelp,
  ChatDepartment,
  hideZendeskWidget,
} from 'common/report/external-widgets/zendesk';
import {UserPersistence} from 'common/report/persistence';
import {activateCommunityWidget, hideCommunityWidget} from 'common/report/external-widgets/insided';
import {moveGlobalActionBar} from 'common/report/external-widgets/util';
import {SubscriptionCallbackParams} from 'common/report/stores/store';
import {UserIconEl} from 'common/widgets/user-icon';
import {layoutManager} from 'common/report/util/layout-manager';
import {IconSizeType} from 'common/constants';

import {NavigationTrackingEvent} from './reports-menu/tracking';
import {ChromeHeaderState, NavCategories, NavReports, OrgInfo, ReportPersistenceCookie} from './types';
import {HelpLabels, HelpTrackingEvent, NavCategoryType, Report} from './constants';
import {getNavCategories, getNavReportsFromCategory} from './util';
import {template} from './template';
import css from './index.styl?inline';
import './reports-menu';
import './festive-mode-header-anim';

const REPORT_PERSISTENCE_COOKIE = `mp_persistence`;

const TOAST_TTL = 7000;
const toastCtaCallbackMap = new Map<string, () => void>();

const SETTINGS_CATEGORY_TRACKING = {
  Org: events.viewOrg,
  Account: events.viewAccountSettings,
  Project: events.viewProjectSettings,
  Workspace: events.viewWorkspaceSettings,
};

export interface ChromeHeaderAttrs {
  /** used by dashboards to hide chrome-header in tv mode */
  hidden: boolean;
}

export class ChromeHeader extends Component<ChromeHeaderState, ChromeHeaderAttrs> {
  static get attrsSchema(): AttrsSchema<ChromeHeaderAttrs> {
    return {
      hidden: `boolean`,
    };
  }

  get config() {
    const defaultState: ChromeHeaderState = {
      categories: null,
      createProjectError: null,
      createProjectName: null,
      createProjectSelectedOrgId: null,
      currentCategoryName: ``,
      currentUrlPath: ``,
      currentReportName: ``,
      errorAlertMsg: null,
      inviteModalOrgs: [],
      isAtProjectMax: false,
      isOpen: {
        applicationsMenu: false,
        createProjectModal: false,
        helpMenu: false,
        inviteModal: false,
        searchModal: false,
        projectMenu: false,
        searchMenu: false,
        setupMenu: false,
        userMenu: false,
      },
      isSupportWidgetMaximized: false,
      loading: {
        platformApps: false,
      },
      orgData: [],
      platformApps: null,
      projectId: null,
      projectMenuWorkspaces: [],
      searchedText: DEFAULT_SEARCH_QUERY_STRING,
      selectedCategory: ``,
      showIntroModal: false,
      showNewProjectInput: false,
      showSupportWidget: false,
      userStore: userDetailsStore,
      workspaceId: null,
    };

    return {
      css,
      template,
      useShadowDom: true,
      defaultState,
      helpers: {
        getReportUrl,
        getFestiveMode,
        stickyElemHooks,

        currentProjectName: () => this.currentProject?.name || ``,
        featuresStoreHasValue: () => featuresStore.value !== null,
        hasProjectFeatureFlag: (flag: ProjectFeatureFlag) => featuresStore.value?.hasProjectFeatureFlag(flag) ?? false,

        shouldDisplayNavigationOptions: () => {
          const {projectId, workspaceId, currentReportName, categories} = this.state;
          // this happens when user has first signed up and they don't have any projects to navigate to
          if (!projectId) {
            return false;
          }

          // don't show navigation options if we don't have categories to show
          if (!categories) {
            return false;
          }

          // hides navigation options when:
          // 1. a user is in Data View library but we can't infer a workspaceId
          // 2. a user does not belong to any data views
          // when a project contains workspaces, we should always navigate to workspace based reports
          if (currentReportName === `views` && workspaceId === null) {
            return false;
          }

          return true;
        },

        allMenusClosed: () => !Object.values(this.state.isOpen).find((menu) => menu),

        updateOpen: (updated?: ChromeHeaderState['isOpen']) => {
          if (updated) {
            this.update({isOpen: {...this.state.isOpen, ...updated}});
          } else {
            // if not provided a specific update, just close everything
            this.update({
              isOpen: Object.keys(this.state.isOpen).reduce((o, key) => Object.assign(o, {[key]: false}), {}),
            });
          }
        },

        getProjectAndWorkspaceName: (truncated: boolean) => {
          const projectName = this.currentProject?.name || `Loading ...`;
          const workspaceName =
            this.helpers.areWorkspacesActivated(this.currentProject?.id) &&
            (this.helpers.getCurrentWorkspace()?.name || ``);
          const separator = ` · `;

          if (truncated) {
            // maxCharLen constant accomodates the project selector such that chrome-header doesn't overflow 1150px.
            // 1150px is the minimum viewport width we support defined in global.styl
            const maxCharLen = 46;
            if (workspaceName) {
              const truncatedWorkspaceName = truncateMiddle(workspaceName, maxCharLen / 2);
              const remainingLen = maxCharLen - truncatedWorkspaceName.length - separator.length;
              const truncatedProjectName = truncateMiddle(projectName, remainingLen);
              return truncatedProjectName + separator + truncatedWorkspaceName;
            } else {
              return truncateMiddle(projectName, maxCharLen);
            }
          } else {
            return workspaceName ? projectName + separator + workspaceName : projectName;
          }
        },

        getProjectItems: (projects: ProjectDetails[]): SectionItem[] =>
          projects
            .map((proj) => {
              const workspaceCount = this.getWorkspacesForProject(proj.id).length;
              const workspaceOverrides = this.helpers.areWorkspacesActivated(proj.id)
                ? {
                    subLabel: `${workspaceCount} ${pluralize(`Data View`, workspaceCount)}`,
                    subLabelTheme: `soft`,
                    hasCaret: workspaceCount > 0,
                  }
                : {};

              return {
                label: proj.name || `My New Project`,
                subLabel: proj.isRollupProject() && this.state.orgData.length > 1 ? `Rollup Project` : ``,
                subLabelTheme: `strong`,
                projectId: proj.id,
                type: proj.type,
                ...workspaceOverrides,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label)),

        selectProjectMenuBackClick: () => this.update({projectMenuWorkspaces: []}),

        getProjectsForMenu: (): ProjectDetails[] => excludeDemos(Object.values(projectDetailsStore.value)),
        getProjectsMenuSections: (): Section[] => {
          const orgData = excludeDemos(this.state.orgData || []);
          const allProjects = this.helpers.getProjectsForMenu();
          const projectMenuWorkspaces = this.state.projectMenuWorkspaces;
          let sections: Section[];

          if (this.helpers.areWorkspacesActivated()) {
            sections = [
              {
                items: projectMenuWorkspaces.map((workspace) => ({
                  label: workspace.name,
                  id: workspace.id,
                  projectId: workspace.projectId,
                  description: workspace.description,
                  isWorkspace: true,
                  createdBy: workspace.creator,
                })),
              },
              {
                items: [
                  {
                    label: `Data View Library`,
                    icon: `open`,
                    projectId: projectMenuWorkspaces[0].projectId,
                    href: getReportUrl(`views`, {projectId: projectMenuWorkspaces[0].projectId}),
                  },
                ],
              },
            ];
          } else if (orgData.length > 1) {
            sections = orgData.map((org) => ({
              label: orgData.length > 1 ? org.name : null,
              items: this.helpers.getProjectItems(
                allProjects.filter((p: ProjectDetails) => p.organizationId === org.id),
              ),
            }));
          } else {
            const projects = allProjects.filter((p) => !p.isRollupProject());
            const rollupProjects = allProjects.filter((p) => p.isRollupProject());
            const hasRollupProjects = rollupProjects.length > 0;
            sections = [
              {
                // Only explicitly label if there are rollup projects, otherwise its just a single list
                label: hasRollupProjects ? `Projects` : ``,
                items: this.helpers.getProjectItems(projects),
              },
            ];

            if (hasRollupProjects) {
              sections.push({
                label: `Rollup Projects`,
                items: this.helpers.getProjectItems(rollupProjects),
              });
            }
          }
          sections.push({
            label: `Mixpanel Demo`,
            items: [
              {
                label: `Sample Datasets`,
                icon: `open`,
                projectId: 0,
                onClick: () => {
                  openDemoProjectSelectorModal((ev) => {
                    this.addToast(ev.detail.message, ToastType.ERROR);
                  });
                },
                trackEvent: `[Demo Project] Click Explore Demo`,
                trackProps: {'Navigated From': `Project Menu`},
              },
            ],
          });

          // cap sections to max of 10 items so list isn't insanely long
          for (const section of sections) {
            section.showLimit = section.items.length > 10 ? 5 : 10;
          }

          return sections;
        },

        getProjectOrWorkspaceContextPanelData: (item: SectionItem): ContextPanelData | null => {
          if (item.isWorkspace && item.description) {
            return {
              header: item.label,
              subheader: item.createdBy ? `Created by ${item.createdBy}` : ``,
              headerLink: this.currentProject?.hasPermission(`access_project_settings`)
                ? `/settings/project/${item.projectId}#views/detail/${item.id}`
                : ``,
              description: item.description,
            };
          }
          return null;
        },

        handleSearchTermSelected: (ev) => {
          this.helpers.updateOpen({searchModal: true});
          this.update({searchedText: ev.detail.searchedText});
        },

        getHelpMenuSections: (): Section[] => {
          const {supportChatEnabled} = supportInfoStore.value;
          return [
            {
              items: [
                {
                  label: HelpLabels.ZendeskHelp,
                  icon: `search`,
                },
                {
                  label: HelpLabels.LaunchTour,
                  isDisabled: !this.getTourInfo(),
                },
                {
                  label: HelpLabels.CommunityWidget,
                },
                {
                  label: HelpLabels.Suggestion,
                  href: `https://community.mixpanel.com/topic/new?fid=15`,
                  target: `_blank`,
                },
                {
                  label: HelpLabels.Intro,
                },
              ].filter((item) => !item.isDisabled),
            },
            {
              items: (this.currentProject?.externalHelpLinks || []).map((link) => ({
                label: link.linkName,
                href: link.linkURL,
                target: `_blank`,
                isDisabled: !link.linkName,
                icon: this.getHelpLinkIcon(link.linkURL),
              })),
            },
            {
              items: [
                {
                  label: HelpLabels.LiveChat,
                  isDisabled: !supportChatEnabled,
                  icon: `sms`,
                },
                {
                  label: HelpLabels.ContactSupport,
                  icon: `email`,
                },
              ].filter((item) => !item.isDisabled),
            },
          ];
        },

        /** areWorkspacesActivated a boolean to indicate the project has actually started to utilize workspaces[dataviews] */
        areWorkspacesActivated: (projectId?: number) => {
          const projectMenuWorkspaces = !projectId
            ? this.state.projectMenuWorkspaces
            : this.getWorkspacesForProject(projectId);

          return (
            projectMenuWorkspaces.length > 1 ||
            (projectMenuWorkspaces.length === 1 &&
              projectDetailsStore.value[projectMenuWorkspaces[0].projectId]?.hasWorkspaces)
          );
        },
        getCurrentWorkspace: () => this.currentProject?.workspaces[this.state.workspaceId],

        updateCurrentCategoryAndReportNameFromUrl: () => {
          const reportInfo = this.getReportInfoFromUrl(new URL(window.location.href));
          if (reportInfo) {
            this.update({
              currentCategoryName: reportInfo.categoryName,
              currentReportName: reportInfo.reportName,
            });
            this.persistLastSeenUrlData();
          }
        },

        shouldShowSecondaryHeader: (): boolean => {
          const {categories, currentCategoryName} = this.state;
          if (!categories) {
            return false;
          }

          const cat = categories[currentCategoryName];
          // if there's only one report in a category, don't show the secondary header
          return Object.values(cat?.reports || {}).length > 1;
        },

        initializeSpaNavManager: () => {
          if (this.currentProject) {
            // multiple async entry points, use the one that's ready
            spaNavManager.initialize({
              projectId: String(this.state.projectId),
              projectName: this.currentProject.name,
            });
          }
        },

        // TODO
        // eslint-disable-next-line sonarjs/cognitive-complexity
        navigate: (url, ev = null, fromAnchorlink = false) => {
          const {currentUrlPath} = this.state;
          let openInNewTab = false;
          if (ev?.ctrlKey || ev?.metaKey || ev?.detail?.modifierKeys?.ctrl || ev?.detail?.modifierKeys?.meta) {
            // mouse event OR items-menu event
            openInNewTab = true;
          }

          if (openInNewTab) {
            // if anchor link, browser will do the right thing by opening in new tab anyway
            if (!fromAnchorlink) {
              window.open(url, `_blank`);
            }
          } else {
            if (spaNavManager.isNavigable(url, currentUrlPath)) {
              // this will be a fake page navigation by swapping the iron app js bundle and css
              // we'll ask the window's beforeunload whether we should ask the user to stay on the page
              let shouldNavigate = true;
              if (window.onbeforeunload instanceof Function) {
                const beforeUnloadEv = new CustomEvent(`beforeunload`, {cancelable: true});
                window.onbeforeunload(beforeUnloadEv);
                if (beforeUnloadEv.defaultPrevented) {
                  shouldNavigate = confirm(`Changes you made may not be saved. Leave this page?`);
                }
              }

              if (shouldNavigate) {
                spaNavManager.navigate(url, currentUrlPath, ev);
                this.helpers.updateCurrentCategoryAndReportNameFromUrl();
                this.update({currentUrlPath: url});
              } else {
                // user said no, don't navigate
                if (ev) {
                  if (ev.type === `popstate`) {
                    // popstate event immediately overwrites the url even though the user didn't want to leave
                    // push back the old url
                    window.history.pushState(null, null, currentUrlPath);
                  } else {
                    ev.preventDefault();
                  }
                }
              }
            } else {
              this.update({currentUrlPath: url});

              // if an anchor link is clicked, let the browser do it's thing
              // otherwise onbeforeunload will fire twice if we also set location.href
              // see https://mixpanel.atlassian.net/browse/RNA-737
              if (!fromAnchorlink) {
                window.location.href = url;
              }

              const handleBeforeUnload = (beforeUnloadEv: BeforeUnloadEvent) => {
                // ev.defaultPrevented == true means the user was prompted to stay or leave.
                // we have no way to know what their choice was
                // so don't do an optimistic report whiteout and select the new report tab
                // if user leaves, the right state will be shown anyway after the page loads, just a bit slower
                if (!beforeUnloadEv.defaultPrevented) {
                  // optimistic update that the new page started loading instantly
                  // by showing whiteout loader spinny and selecting the new report in nav
                  this.helpers.updateCurrentCategoryAndReportNameFromUrl();
                  document.querySelector(`.whiteout_loader`).classList.remove(`hidden`);
                }
              };

              window.addEventListener(`beforeunload`, handleBeforeUnload, {once: true});
            }
          }
        },

        handleHistoryStateChange: (ev: Event) => {
          this.helpers.updateCurrentCategoryAndReportNameFromUrl();

          // NOTE: popstate is fired for history forward/back and location hash changes
          // we only navigate if the pathname changes, for hash only changes, let the panel router handle changes
          // otherwise we'll get a 'Maximum call stack size exceeded' error as navigate() causes another popstate
          if (ev.type === `popstate`) {
            const prevPathname = this.state.currentUrlPath.split(`#`)[0];
            const curPathname = window.location.pathname;
            if (curPathname !== prevPathname) {
              this.helpers.navigate(curPathname + window.location.hash, ev);
            }
          }
        },

        selectProjectMenuItem: (ev) => {
          const {item} = ev.detail;
          if (item.projectId) {
            this.helpers.navigateToProject(ev);
          }
          if (item.href || item.onClick) {
            // track, call onClick, and navigate in that order,
            // so that all three can be done if need be
            if (item.trackEvent) {
              mixpanel.track(item.trackEvent, item.trackProps || {});
            }
            if (item.onClick) {
              item.onClick();
            }
            if (item.href) {
              this.helpers.navigate(item.href, ev, true);
            }
          }
        },

        // eslint-disable-next-line sonarjs/cognitive-complexity
        navigateToProject: (ev) => {
          const {item} = ev.detail;
          const projectId = item.projectId;
          const projectDomain = projectDetailsStore.value[projectId].domain;
          const workspaceId = item.isWorkspace ? item.id : get(this.getWorkspacesForProject(projectId)[0], `id`);

          if (item.hasCaret) {
            const projectMenuWorkspaces = this.getWorkspacesForProject(projectId);
            if (projectMenuWorkspaces.length) {
              this.update({projectMenuWorkspaces});
              return;
            }
          }

          this.helpers.updateOpen({projectMenu: false});
          this.update({projectMenuWorkspaces: []});

          // e.g if user is in insights, navigate to insights in new project or workspace
          let navigatePath = makePathFromUrlState({projectId, workspaceId});

          // if switching between workspaces for the same project, preserve the hash for reports to keep selected user report
          if (
            navigatePath &&
            `${workspaceId}` !== `${this.state.workspaceId}` &&
            `${projectId}` === `${this.state.projectId}`
          ) {
            navigatePath += window.location.hash;
          }

          if (!isPersistableReport() || !navigatePath) {
            // let django navigate to last persisted report
            // trailing slashes are important otherwise we get an extra 301 roundtrip
            navigatePath = workspaceId ? `/report/${projectId}/view/${workspaceId}/` : `/report/${projectId}/`;
          }

          if (workspaceId) {
            const allProjectWorkspaces = this.getWorkspacesForProject(projectId);
            const hasGlobalWorkspaceAccess = allProjectWorkspaces.some((w) => w.isGlobal);
            const hasDefaultWorkspaceAccess = allProjectWorkspaces.some((w) => w.isDefault);
            const selectedWorkspace: WorkspaceObj = allProjectWorkspaces.find(
              (w: WorkspaceObj) => w.id === workspaceId,
            );
            mixpanel.track(`Selected Workspace`, {
              'Selected Project ID': projectId,
              'Selected Workspace ID': workspaceId,
              'Selected Workspace Name': ev.detail.item.label,
              'Selected Workspace Is Global': selectedWorkspace.isGlobal,
              'Selected Workspace Is Default': selectedWorkspace.isDefault,
              'User Has Global Workspace Access': hasGlobalWorkspaceAccess,
              'User Has Default Workspace Access': hasDefaultWorkspaceAccess,
              'Total Available Workspaces': allProjectWorkspaces.length,
              // track url pattern e.g /report/<pid>/view/<wid>/insights. remove trailing slashes
              'Navigate Url Pattern': navigatePath
                .replace(projectId, `<pid>`)
                .replace(workspaceId, `<wid>`)
                .replace(/\/$/, ``),
            });
          } else {
            mixpanel.track(`Selected Project`, {
              'Selected Project ID': projectId,
              'Selected Project Name': ev.detail.item.label,
              'Selected Project Type': ev.detail.item.type === ProjectType.Rollup ? `Rollup Project` : `Project`,
              // track url pattern e.g /report/<pid>/insights. remove trailing slashes
              'Navigate Url Pattern': navigatePath.replace(projectId, `<pid>`).replace(/\/$/, ``),
            });
          }

          // This logic only applies when we are explicitly switching projects and not report types,
          // so we keep it out of helpers.navigate to avoid an adding another argument to a complex function.
          // we only want to switch domains for staging and prod i.e (x.)?mixpanel.com domains,
          // not for devboxes or localhost standalone mode
          const curDomain = window.location.hostname;
          const isMixpanelDotCom = (domain: string) => /\bmixpanel\.com$/.test(domain);
          if (isMixpanelDotCom(curDomain) && projectDomain && projectDomain !== curDomain) {
            window.location.assign(`//` + projectDomain + navigatePath);
          } else {
            this.helpers.navigate(navigatePath, ev);
          }
        },

        createProjectModalChange: ({detail}) => {
          this.helpers.updateOpen({createProjectModal: detail.state === Visibility.Open});
        },
        clickedCreateProject: () => {
          if (this.state.orgData.length > 1) {
            this.helpers.updateOpen({createProjectModal: true, projectMenu: false});
          } else {
            this.update({showNewProjectInput: true});
          }
        },

        selectOrg: (orgId) => this.update({createProjectSelectedOrgId: orgId}),

        updateCreateProjectName: (ev) => this.update({createProjectName: ev.target.value}),

        clickedCancel: () => this.helpers.updateOpen({createProjectModal: false}),

        projectNameValid: () => Boolean(this.state.createProjectName) && Boolean(this.state.createProjectName.trim()),

        clickedCreate: async () => {
          try {
            const resp = await createOrgProjects(this.state.createProjectSelectedOrgId, [this.state.createProjectName]);
            this.helpers.updateOpen({projectMenu: false});
            const newProjectId = resp[0].id;
            this.helpers.navigate(`/report/${newProjectId}/`);
          } catch (error) {
            this.update({createProjectError: error});
          }
        },

        getOrgsWithCreateProjectPermission: () =>
          this.state.orgData.filter((org) => this.orgHasPerm(org, `create_project`)),

        orgHasPerm: (org, perm) => this.orgHasPerm(org, perm),

        userInitials: () => nameToInitials(this.state.userStore.value.name),

        getOrgs: ({permissions = null} = {}) =>
          this.state.orgData.filter(
            (o) => !permissions || permissions.every((p) => o.permissions && o.permissions.includes(p)),
          ),

        menuItemSelected: (ev) => {
          const {item} = ev.detail;
          if (item.href) {
            this.trackSettingsMenuItemSelected(item);
            this.helpers.navigate(item.href, ev, true);
          } else {
            switch (item.label) {
              case `Log Out`: {
                // Clear out LocalStorage persistence
                window.localStorage.removeItem(`mp_persistence`);

                // it's a security risk if User B can see User A's cached responses
                // purge fetch-cache when user logs out
                purgeFetchCache({everything: true});
                UserPersistence.purge();

                mixpanel.reset();
                // TODO @noj: ask around if we still need idmgmnt tracking, it's not initialized for volt
                mixpanel.idmgmt?.reset();
                window.location.href = `/logout`;
                break;
              }
              case `Invite Users`: {
                const orgUrlMatch = /^\/settings\/org\/(\d+)/.exec(window.location.pathname);
                const orgId = parseInt(orgUrlMatch && orgUrlMatch[1], 10) || null;
                const orgs = this.helpers.getOrgs({permissions: [`add_user`]});
                const org = orgs.find((o) => o.id === orgId);
                // if org id is specified by url and that org has `add_user` permission, show
                // invite modal for that org; otherwise show modal for all orgs with `add_user`
                // (user will choose org in modal)
                const inviteModalOrgs = org ? [org] : orgs;
                if (inviteModalOrgs.length) {
                  this.helpers.updateOpen({inviteModal: true});
                  this.update({inviteModalOrgs});
                }
              }
            }
          }
        },

        closeSupportWidget: () => {
          moveGlobalActionBar({isWidgetPresent: false});
          this.update({showSupportWidget: false});
        },

        helpMenuItemSelected: (ev) => {
          const {item} = ev.detail;
          switch (item.label) {
            case HelpLabels.ZendeskHelp:
              mixpanel.track(HelpTrackingEvent.ZendeskHelp);
              this.hideAllWidgets();
              activateZendeskHelp();
              break;
            case HelpLabels.CommunityWidget:
              mixpanel.track(HelpTrackingEvent.CommunityWidget);
              this.hideAllWidgets();
              activateCommunityWidget();
              break;
            case HelpLabels.AskQuestions:
            case HelpLabels.ContactSupport:
              mixpanel.track(HelpTrackingEvent.Support);
              this.hideAllWidgets();
              moveGlobalActionBar({isWidgetPresent: true});
              this.update({showSupportWidget: true});
              break;
            case HelpLabels.LiveChat:
              this.hideAllWidgets();
              activateZendeskChat(
                ChatDepartment.Support,
                this.state.userStore.value.name,
                this.state.userStore.value.email,
              );
              break;
            case HelpLabels.LaunchTour:
              this.launchAndTrackTour();
              break;
            case HelpLabels.Intro:
              mixpanel.track(HelpTrackingEvent.Intro);
              this.update({showIntroModal: true});
              break;
            case HelpLabels.Suggestion:
              mixpanel.track(HelpTrackingEvent.Suggestion);
              break;
            default:
              mixpanel.track(HelpTrackingEvent.ExternalLink, {
                'External Link Name': item.label,
                'External Link URL': item.href,
              });
              break;
          }
        },

        closeInviteModal: () => {
          this.helpers.updateOpen({inviteModal: false});
        },

        onIntroModalChange: ({detail}) => {
          this.update({showIntroModal: detail.state === Visibility.Open});
        },

        closeIntroModal: () => {
          this.update({showIntroModal: false});
        },

        menuChanged: (menuState: string, menuType: string) => {
          if (menuState === `open`) {
            mixpanel.track(NavigationTrackingEvent.OpenPrimaryMenu, {Menu: menuType});
            this.update({
              [`${menuType}MenuOpen`]: true,
              selectedCategory: ``,
            });
          } else if (menuState === `closed`) {
            this.update({
              [`${menuType}MenuOpen`]: false,
              selectedCategory: ``,
            });
          }
        },

        handleSearchMenuChange: (ev: CustomEvent) => {
          const {isOpen, searchedText} = ev.detail;
          this.helpers.selectMenuChanged(`search`, isOpen);
          this.update({searchedText});
        },

        selectMenuChanged: (menuType: string, isOpen: boolean) => {
          if (menuType === `project` && isOpen) {
            this.update({projectMenuWorkspaces: this.getWorkspacesForProject(this.state.projectId)});
          }
          this.helpers.updateOpen({[`${menuType}Menu`]: isOpen});
          if (isOpen) {
            mixpanel.track(NavigationTrackingEvent.OpenPrimaryMenu, {
              Menu: menuType,
              [SearchTrackedProperty.PhaseVersion]: this.getSearchPhaseVersion(),
              ...(menuType === `search` ? this.getSearchCommonProperties() : null),
            });
          }
        },

        selectCategory: (catName: string) => {
          if (catName) {
            mixpanel.track(NavigationTrackingEvent.OpenPrimaryMenu, {
              Menu: catName,
              [SearchTrackedProperty.PhaseVersion]: this.getSearchPhaseVersion(),
            });
          }
          this.helpers.updateOpen({
            applicationsMenu: false,
            projectMenu: false,
            setupMenu: false,
            userMenu: false,
          });
          this.update({selectedCategory: catName});
        },

        handleCategoryCloseMenu: () => {
          // race condition with toggleSelectedCategory, click twice to go to another menu rather than once
          this.update({selectedCategory: ``});
          this.helpers.updateOpen();
        },

        toggleSelectedCategory: (catName: string) => {
          // open and close menu if header is clicked
          const selectedCategory = this.state.selectedCategory !== catName ? catName : ``;
          this.helpers.selectCategory(selectedCategory);
        },

        handleClickReportName: (reportName: string, source: string) => {
          mixpanel.track(NavigationTrackingEvent.SelectReport, {
            'Nav Source': source,
            'Report From': this.state.currentReportName,
            'Report To': reportName,
          });
        },

        handleAlertChange: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({errorAlertMsg: null});
          }
        },

        handleNotify: (ev) => {
          const {message} = ev.detail;
          this.addToast(message);
        },

        handleError: (ev) => {
          const {error, message} = ev.detail;
          this.showError(message || error.message, error);
        },

        handleLinkClick: (ev: MouseEvent) => {
          const target = ev.target as HTMLElement;
          if (target && target.closest) {
            const anchorLink: HTMLAnchorElement = closestReportLink(target);
            // let browser handle open in new tab interaction for a(target=_blank)
            if (anchorLink && anchorLink.target !== `_blank`) {
              this.helpers.navigate(anchorLink.getAttribute(`href`), ev, /* fromAnchorlink */ true);
            }
          }
        },

        showNewNav: (): boolean => INTERNAL_PROJECTS.has(this.state.projectId),
        showMessagesMenu: (): boolean =>
          featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.MessagesAndExperiments) ||
          !featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.HideMenuMessages) ||
          !this.currentProject?.isDemo,

        getNavReports: (category: NavCategoryType): NavReports => {
          const {workspaceId, projectMenuWorkspaces, projectId} = this.state;
          const isProjectOrGlobalWorkspace = this.isProjectOrGlobalWorkspace(workspaceId, projectMenuWorkspaces);
          return getNavReportsFromCategory(category, featuresStore.value, isProjectOrGlobalWorkspace, projectId);
        },

        chromeHotKeySwitcher: (ev) => {
          const appVersion = window.navigator.appVersion.includes(`Mac`);
          const key = appVersion ? `Meta+KeyK` : `Ctrl+KeyK`;
          const cmdOrCtrl = appVersion ? `Cmd` : `Ctrl`;
          return keyEventSwitcher(
            {
              [key]: () => {
                mixpanel.track(NavigationTrackingEvent.OpenPrimaryMenu, {
                  Menu: `search`,
                  ...this.getSearchCommonProperties(),
                  [SearchTrackedProperty.ShortcutDisplayName]: `${cmdOrCtrl}K`,
                });
                this.helpers.selectMenuChanged(`search`, !this.state.isOpen.searchMenu);
              },
            },
            {stopPropagation: true},
          )(ev);
        },

        getSearchShortcutDisplayName: () => `${this.helpers.getPreferredKey()}K`,

        handleSearchModalClose: () => {
          this.helpers.updateOpen({searchModal: false});
          this.update({searchedText: DEFAULT_SEARCH_QUERY_STRING});
        },

        getApplicationsMenuSections: (): Section[] => {
          const {platformApps, projectId, workspaceId, loading} = this.state;
          const currentWorkspace = this.helpers.getCurrentWorkspace();
          const currentWorkspaceIsNotGlobal = currentWorkspace && !currentWorkspace.isGlobal;
          const features = featuresStore.value;

          // only a handful of projects have any platform apps, rather than triggering a fetch request for every pageload
          // lazy load platform apps when user clicks on the menu, it is not a frequently clicked menu.
          if (!platformApps && !loading.platformApps) {
            this.update({loading: {...loading, platformApps: true}});
            getPlatformApps({projectId, includeBuildCustomApp: true}).then((platformApps) => {
              this.update({platformApps, loading: {...loading, platformApps: false}});
            });
          }

          const toSectionItem = (app: PlatformApp) => ({
            label: app.name || ``,
            subLabel: [app.description, app.created_by ? `Created by ${app.created_by}.` : null]
              .filter(Boolean)
              .join(`. `),
            wrapSubLabel: true,
            href: app.url,
          });

          return [
            {
              label: `Mixpanel Apps`,
              items: [
                {
                  name: `JQL`,
                  description: `Write powerful queries to analyze your data in Mixpanel.`,
                  url: getReportUrl(`jql-console`, {projectId, workspaceId: this.state.workspaceId}),
                  // JQL would allow users to bypass data filters, it should be hidden for workspaces
                  hidden:
                    features.hasProjectFeatureFlag(ProjectFeatureFlag.HideReportJqlConsole) ||
                    currentWorkspaceIsNotGlobal,
                },
                {
                  name: `Segmentation`,
                  description: `Measure the actions that people take.`,
                  url: getReportUrl(`segmentation`, {projectId}),
                  // hide segmentation for new projects as of 15th Jan 2019
                  hidden:
                    projectId > 1_894_797 || features.hasProjectFeatureFlag(ProjectFeatureFlag.HideReportSegmentation),
                },
                {
                  name: `Revenue`,
                  description: `Easily understand and analyze the revenue you make from individual customers.`,
                  url: getReportUrl(`revenue`, {projectId, workspaceId}),
                  hidden:
                    features.hasProjectFeatureFlag(ProjectFeatureFlag.HideReportRevenue) || currentWorkspaceIsNotGlobal,
                },
              ]
                .filter((app) => !app.hidden)
                .map(toSectionItem),
            },
            {
              label: `Custom Apps`,
              isLoading: loading.platformApps,
              items: platformApps ? platformApps.map(toSectionItem) : [],
            },
          ];
        },

        // eslint-disable-next-line sonarjs/cognitive-complexity
        getSettingsMenuSections: (): Section[] => {
          const currentProject = this.currentProject;
          const organizations = (userDetailsStore.value.isStaff // show demo settings to staff
            ? this.state.orgData
            : excludeDemos(this.state.orgData || [])
          )
            .map((org) => ({
              org,
              label: org.name,
              href: `/settings/org/${org.id}`,
              tracking: SETTINGS_CATEGORY_TRACKING.Org,
              isDisabled: !this.orgHasPerm(org, `view_organization_settings`),
              subLabel: this.orgHasPerm(org, `view_organization_settings`)
                ? null
                : `Your role does not allow you to view settings`,
            }))
            .sort((a, b) => {
              // current org to top
              if (a.org.id === currentProject.organizationId) {
                return -1;
              }
              if (b.org.id === currentProject.organizationId) {
                return 1;
              }
              // alpha sort if both are same state
              if (a.isDisabled === b.isDisabled) {
                return a.label.localeCompare(b.label);
              }
              // disabled to the bottom, enabled at the top
              return a.isDisabled ? 1 : -1;
            });

          let projectSection: Section[] = [];
          if (currentProject?.hasPermission(`access_project_settings`)) {
            const projectId = currentProject.id;
            projectSection = [
              {
                label: `project settings`,
                items: [
                  {
                    label: currentProject.name,
                    href: `/settings/project/${projectId}`,
                    value: currentProject.name,
                    tracking: SETTINGS_CATEGORY_TRACKING.Project,
                  },
                  !currentProject.isRollupProject() &&
                  featuresStore.value?.getFeatureGate(FeatureGate.Workspaces) &&
                  currentProject?.hasPermission(`manage_workspaces`)
                    ? {
                        label: `Manage Data Views`,
                        href: `/settings/project/${projectId}#views`,
                        value: `Manage Workspaces`,
                        tracking: SETTINGS_CATEGORY_TRACKING.Workspace,
                      }
                    : null,
                  {
                    label: `Set up Mixpanel`,
                    href: getReportUrl(`setup/quick`, {projectId}),
                    value: `Install Mixpanel`,
                  },
                  {
                    label: `Codeless Tracking`,
                    href: getReportUrl(`autotrack`, {projectId}),
                    value: `Codeless Tracking`,
                  },
                ].filter(Boolean),
              },
            ];
          }
          const orgsLimit = projectSection.length > 0 ? 3 : 6;

          const profileName = this.state.userStore.value.name || `Personal settings`;
          const roleName = capitalize(this.currentProject?.role?.name || `----`);
          return [
            {
              iconRenderer: ({id, name}: SectionItem) =>
                UserIconEl({attrs: {id, name, 'disable-tooltip': true, size: IconSizeType.Medium}}),
              items: [
                {
                  label: `${profileName} (${roleName})`,
                  id: this.state.userStore.value.id,
                  name: profileName,
                  href: `/settings/account`,
                  value: `Profile & Preferences`,
                  tracking: SETTINGS_CATEGORY_TRACKING.Account,
                },
              ],
            },
            {
              label: `organization settings`,
              items: organizations,
              showLimit: organizations.length > orgsLimit ? orgsLimit - 1 : 0, // prevent-off by one showLimit
            },
            ...projectSection,
            this.helpers.getOrgs({permissions: [`add_user`]}).length
              ? {items: [{label: `Invite Users`, icon: `invite`}]}
              : null,
            {
              items: [{label: `Log Out`}],
            },
          ].filter(Boolean);
        },

        handleToastCtaAction: (ev) => {
          ev.preventDefault();
          const selectedToastId = ev.target.dataset.id;
          if (selectedToastId && toastCtaCallbackMap.has(selectedToastId)) {
            toastCtaCallbackMap.get(selectedToastId).call(null);
            toastCtaCallbackMap.delete(selectedToastId);
          }
        },

        getUserHasCreateWorkspacePermission: () => {
          const projectId = this.state.projectMenuWorkspaces[0].projectId;
          const {orgData} = this.state;
          const project = projectDetailsStore.value[projectId];
          try {
            return (
              project &&
              this.orgHasPerm(
                orgData.find((org) => org.id === project.organizationId),
                `manage_workspaces`,
              )
            );
          } catch (err) {
            reporter.error(err, {projectId, project, orgIds: orgData.map((org) => org.id)});
            return false;
          }
        },

        handleCreateWorkspaceBtnClick: async () => {
          // grab this now because the menu can collapse behind the modal and will 'reset' to the current project based on attrs
          const projectId = this.state.projectMenuWorkspaces[0].projectId;
          try {
            const {didSave, name, description} = await promptManageWorkspaceModal(
              `Create New Data View`,
              {},
              projectId,
            );
            if (didSave) {
              const workspace = await workspacesClient.createWorkspace(projectId, {name, description});
              projectDetailsStore.value[projectId]?.updateWorkspaces({[workspace.id]: workspace});
              projectDetailsStore.update({});
              if (projectId === this.currentProject?.id) {
                this.currentProject.updateWorkspaces({[workspace.id]: workspace});
                currentProjectStore.update({});
              }
              if (this.state.projectMenuWorkspaces[0] && this.state.projectMenuWorkspaces[0].projectId === projectId) {
                // the already computed and sorted project menu needs to be updated with the new workspace
                this.update({projectMenuWorkspaces: this.getWorkspacesForProject(projectId)});
              }
              const [eventName, eventProps] = workspaceCreateEvent(
                this.getWorkspacesForProject(projectId).length,
                {
                  name,
                  description,
                  projectId,
                },
                UISourceObject.ChromeHeaderCreateWorkspace,
              );
              mixpanel.track(`${workspacesTrackingPrefix} ${eventName}`, eventProps);
              this.addToast(
                `Workspace ${name} Created`,
                ToastType.SUCCESS,
                `Manage`,
                () => (window.location.href = `/settings/project/${workspace.projectId}#views/detail/${workspace.id}`),
              );
            }
          } catch (err) {
            const [eventName, eventProps] = workspaceApiFailure(ApiFailureDescription.Creation, err.message, {
              projectId,
              source: UISourceObject.ChromeHeaderCreateWorkspace,
            });
            mixpanel.track(`${workspacesTrackingPrefix} ${eventName}`, eventProps);
            if (err.message && (err.message as string).includes(`already exists`)) {
              this.addToast(
                `Data View already exists`,
                ToastType.ERROR,
                `Manage Data Views`,
                () => (window.location.href = `/settings/project/${projectId}#views`),
              );
            } else {
              this.showError(`Data view creation failed. Please try again or contact support.`, err);
            }
          }
        },

        getModifierKey: () => {
          if (window.navigator?.appVersion) {
            return window.navigator.appVersion.includes(`Mac`) ? `opt` : `alt`;
          }

          return `opt`;
        },

        getPreferredKey: () => {
          if (window.navigator?.appVersion) {
            return window.navigator.appVersion.includes(`Mac`) ? `⌘` : `Ctrl`;
          }

          return `Ctrl`;
        },

        supportWidgetAttrs: () => {
          const projectDetails = this.currentProject;
          return {
            elref: `supportWidget`,
            'events-plan-id': projectDetails?.eventsPlanID,
            'people-plan-id': projectDetails?.peoplePlanID,
            'feature-gate-name': featuresStore.value?.getFeatureGatePlanName() ?? ``,
            'billing-account': supportInfoStore.value.billingAccount,
            'service-plan-ids': supportInfoStore.value.servicePlanIds,
            'user-age-in-days': supportInfoStore.value.userAgeDays,
            'user-email': this.state.userStore.value.email,
            'user-id': this.state.userStore.value.id,
            'user-name': this.state.userStore.value.name,
            'project-id': this.state.projectId,
          };
        },

        handleSupportWidgetMaximize: (ev) => {
          const {isMaximized} = ev.detail;
          moveGlobalActionBar({isWidgetPresent: !isMaximized});
        },
      },
    };
  }

  handleUserDetails() {
    // update calculated metadata based on global data
    const orgData: OrgInfo[] = Object.values(userDetailsStore.value.organizations);
    const orgsWithCreatePerms = orgData.filter((org) => this.orgHasPerm(org, `create_project`));
    const createProjectSelectedOrgId = orgsWithCreatePerms.length && orgsWithCreatePerms[0].id;

    this.update({
      orgData, // TODO read directly from userDetailsStore without state copy
      createProjectSelectedOrgId,
    });
  }

  getValidWorkspaceId(project: ProjectDetails, workspaceId: number) {
    const userWorkspaces = this.getWorkspacesForProject(project.id) || [];
    const foundWorkspace = userWorkspaces.find((workspace) => workspace.id === workspaceId);
    if (foundWorkspace) {
      return foundWorkspace.id;
    } else {
      // in case workspaceId isn't valid, we try to find any data view a user has access to
      return userWorkspaces.length ? userWorkspaces[0].id : null;
    }
  }

  handleProjectDetails() {
    // if current project has workspaces but we don't have a workspaceId
    // set a workspaceId from mp_persistence cookie or the project's global workspace
    // so when user clicks on reports, they navigate to workspace based pages

    let stateUpdate = {};
    // check if the current workspaceId is still valid. It can be invalid, if a user leaves a workspace
    // in data views library.
    if (this.currentProject) {
      // If a staff user doesn't have access to any workspaces, we keep the workspace_id initialized from initialize()
      // this prevents workspace_id from being changed as a staff user moves from app to app.
      if (userDetailsStore.value.isStaff && !Object.keys(this.currentProject.workspaces).length) {
        this.update(stateUpdate);
        return;
      }

      if (this.getValidWorkspaceId(this.currentProject, this.state.workspaceId) !== this.state.workspaceId) {
        // reset workspace_id
        this.update({workspaceId: null});
      }
      if (!this.state.workspaceId) {
        const workspaceId = this.getValidWorkspaceId(
          this.currentProject,
          this.getWorkspaceIdFromPersistenceCookie(this.currentProject.id) ||
            this.getGlobalWorkspaceIdForProject(this.currentProject.id),
        );
        stateUpdate = {workspaceId};
      }

      // projectMenuWorkspaces
      const projectMenuWorkspaces = this.getWorkspacesForProject(this.currentProject.id);
      if (projectMenuWorkspaces.length) {
        Object.assign(stateUpdate, {projectMenuWorkspaces});
      }
    }
    // the function should always run update()
    this.update(stateUpdate);

    this.helpers.initializeSpaNavManager();
  }

  handleSupportInfo() {
    const {userStore} = this.state;
    if (activatePersistedZendeskChat(ChatDepartment.Support, userStore.value.name, userStore.value.email)) {
      moveGlobalActionBar({isWidgetPresent: true});
    }
    this.update();
  }

  handleFeaturesStoreValueChange({error}: SubscriptionCallbackParams) {
    if (error) {
      const errorMessage = `Failed to load features info`;
      this.addToast(`${errorMessage}. If this error persists, please contact support.`, ToastType.ERROR);
      reporter.error(errorMessage);
    } else {
      this.update({categories: this.getCategories()});
      this.helpers.updateCurrentCategoryAndReportNameFromUrl();

      if (featuresStore.value.hasProjectFeatureFlag(ProjectFeatureFlag.FestiveModeStage)) {
        setFestiveMode(FestiveMode.Luv);
      } else if (featuresStore.value.hasUserFeatureFlag(UserFeatureFlag.FestiveModeStage)) {
        // mostly to delight a twitter user https://twitter.com/JonCarden/status/1287755477082923009
        // see https://mixpanel.slack.com/archives/C0W91MX2Q/p1595864326150400
        setFestiveMode(FestiveMode.HorseRun);
      }
    }
  }

  public connectedCallback() {
    super.connectedCallback();

    viewedReportFlagManager.increment();

    this.initialize = this.initialize.bind(this);
    let isInitialized = false;
    // projectId and workspaceId is present on url 99% of the time but for some url e.g settings, it's not.
    // we have to wait for DOMContentLoaded for those cases
    if (getProjectId()) {
      this.initialize();
      isInitialized = true;
    }

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has(`contact-us`) && searchParams.get(`contact-us`) !== `false`) {
      this.update({
        isSupportWidgetMaximized: true,
        showSupportWidget: true,
      });
    }
    if (searchParams.has(`full-width`)) {
      layoutManager.setConfig({fullWidth: true});
    }

    // mp.report.globals are initialized after chrome-header is connected
    // so we need to wait for DOMContentLoaded
    window.addEventListener(
      `DOMContentLoaded`,
      () => {
        if (!isInitialized) {
          this.initialize();
        }
      },
      {once: true},
    );
    useEventListener(this, window, `chameleonInitialized`, () => this.onChameleonInitialized());
  }

  public initialize() {
    const projectId = parseInt(getProjectId(), 10);
    const workspaceId = parseInt(getWorkspaceId(), 10) || null; // null instead of NaN
    this.update({projectId, workspaceId, currentUrlPath: window.location.pathname});

    // clear out volt shell elements if any. mp-chrome-header's #shadow-root is unaffected
    this.innerHTML = ``;

    this.handleUserDetails = this.handleUserDetails.bind(this);
    this.handleProjectDetails = this.handleProjectDetails.bind(this);
    this.handleSupportInfo = this.handleSupportInfo.bind(this);
    this.handleFeaturesStoreValueChange = this.handleFeaturesStoreValueChange.bind(this);

    userDetailsStore.subscribe(this.handleUserDetails);
    projectDetailsStore.subscribe(this.handleProjectDetails, `all-projects`);
    currentProjectStore.subscribe(this.handleProjectDetails);
    supportInfoStore.subscribe(this.handleSupportInfo);
    featuresStore.subscribe(this.handleFeaturesStoreValueChange);

    supportInfoStore.fetch();
    userDetailsStore.fetch();

    // projectId could be empty if user does not have access to any project
    // see https://mixpanel.slack.com/archives/C014L0QMH1T/p1592860852025000
    if (projectId) {
      currentProjectStore.fetch();
      featuresStore.fetch({projectId});
    } else {
      reporter.info(`Cannot fetch current project id`, {url: window.location.href});
    }

    this.handleUserDetails();
    projectDetailsStore.fetch({}, {}, `all-projects`);

    const projectMenuWorkspaces = this.getWorkspacesForProject(projectId);
    this.update({projectMenuWorkspaces});

    this.helpers.initializeSpaNavManager();

    window.addEventListener(`popstate`, this.helpers.handleHistoryStateChange);
    window.addEventListener(`pushstate`, this.helpers.handleHistoryStateChange);
    window.addEventListener(`replacestate`, this.helpers.handleHistoryStateChange);
    document.addEventListener(`click`, this.helpers.handleLinkClick);
    this.el.addEventListener(`click`, this.helpers.handleLinkClick); // catch link clicks within shadow dom
    document.addEventListener(`keydown`, this.helpers.chromeHotKeySwitcher);
  }

  public disconnectedCallback() {
    userDetailsStore.unsubscribe(this.handleUserDetails);
    currentProjectStore.unsubscribe(this.handleProjectDetails);
    projectDetailsStore.unsubscribe(this.handleProjectDetails, `all-projects`);
    featuresStore.unsubscribe(this.handleFeaturesStoreValueChange);

    window.removeEventListener(`popstate`, this.helpers.handleHistoryStateChange);
    window.removeEventListener(`pushstate`, this.helpers.handleHistoryStateChange);
    window.removeEventListener(`replacestate`, this.helpers.handleHistoryStateChange);
    document.removeEventListener(`click`, this.helpers.handleLinkClick);
    this.el.removeEventListener(`click`, this.helpers.handleLinkClick);
    document.removeEventListener(`keydown`, this.helpers.chromeHotKeySwitcher);

    super.disconnectedCallback();
  }

  get currentProject(): ProjectDetails | null {
    return currentProjectStore.value;
  }

  private getCategories(): NavCategories {
    const {workspaceId, projectMenuWorkspaces, projectId} = this.state;
    const isProjectOrGlobalWorkspace = this.isProjectOrGlobalWorkspace(workspaceId, projectMenuWorkspaces);
    // getCategories is only called once featuresStore has value, so it's safe to access without ?.
    const features = featuresStore.value;
    return getNavCategories({
      features,
      isDemo: this.currentProject?.isDemo,
      isProjectOrGlobalWorkspace,
      projectId,
    });
  }

  private getWorkspacesForProject(projectId: number): WorkspaceObj[] {
    const workspaces =
      projectId === this.currentProject?.id
        ? this.currentProject?.workspaces
        : projectDetailsStore.value[projectId]?.workspaces;
    return Object.values(workspaces ?? {}).sort(sortWorkspaces);
  }

  private isProjectOrGlobalWorkspace(workspaceId: number, projectMenuWorkspaces: WorkspaceObj[]) {
    if (projectMenuWorkspaces) {
      const workspace = projectMenuWorkspaces.find((ws) => ws.id === workspaceId);
      return !workspace || workspace.isGlobal;
    }
    return true;
  }

  private orgHasPerm(org: OrgInfo, perm: string) {
    if (!org || !perm) {
      return false;
    } else {
      return Array.isArray(org.permissions) && org.permissions.includes(perm);
    }
  }

  private trackSettingsMenuItemSelected(item) {
    const {org, tracking} = item;
    if (tracking) {
      if (tracking === events.viewOrg && org) {
        track(tracking, {
          [properties.orgId]: org.id,
          [properties.orgName]: org.name,
        });
      } else {
        track(tracking);
      }
    }
  }

  private addToast(message: string, toastType?: ToastType, ctaLabel?, ctaCallback?) {
    const newToast = {message, ttl: TOAST_TTL, type: toastType, cta: ctaLabel, ctaCallback};
    addToast(newToast);
  }

  private showError(errorAlertMsg: string, err: Error) {
    if (err) {
      console.error(err);
    }
    this.update({errorAlertMsg});
  }

  private getReportInfoFromUrl(url: URL): {categoryName: string; reportName: string; reportEndpoint: string} | null {
    // go through every report and see if we have an endpoint match
    for (const [categoryName, category] of Object.entries(this.state.categories || {})) {
      for (const [reportName, report] of Object.entries(category.reports)) {
        const [endpointPath, endpointHash] = report.endpoint.split(`#`);

        // allow trailing hash in endpoint match
        const hasEndpointMatch =
          url.pathname.endsWith(`/${endpointPath}`) ||
          url.pathname.endsWith(`/${endpointPath}/`) ||
          report.endpointAliases?.some(
            (alias: string) => url.pathname.endsWith(`/${alias}`) || url.pathname.endsWith(`/${alias}/`),
          );
        const hasHashMatch = !endpointHash || url.hash.startsWith(`#${endpointHash}`);
        if (hasEndpointMatch && hasHashMatch) {
          return {
            categoryName,
            reportName,
            reportEndpoint: report.endpoint,
          };
        }
      }
    }

    // if endpoint isn't found in categories tree
    const reportEndpoint = parseStateFromUrl(url.pathname)?.appName;
    if (reportEndpoint) {
      return {
        categoryName: ``,
        reportName: reportEndpoint,
        reportEndpoint,
      };
    }

    return null;
  }

  private async persistLastSeenUrlData(): Promise<void> {
    const url = window.location.href;
    const reportInfo = this.getReportInfoFromUrl(new URL(url));
    if (!isPersistableReport()) {
      debug.warn(`Current report not persistable`);
    } else if (!reportInfo) {
      reporter.warn(`Report info not found for given URL`, {url});
    } else {
      await safeSetCookie(
        REPORT_PERSISTENCE_COOKIE,
        JSON.stringify({
          project: this.state.projectId,
          ...(this.state.workspaceId ? {workspace: this.state.workspaceId} : {}),
          report_path: reportInfo.reportEndpoint,
        } as ReportPersistenceCookie),
        14 /*days*/,
      );
    }
  }

  private getHelpLinkIcon(href) {
    const {hostname} = new URL(href);
    return /.?slack.com/.test(hostname) ? `slack-logo` : `share`;
  }

  private getWorkspaceIdFromPersistenceCookie(projectId: number): number | null {
    try {
      const persistenceCookie: ReportPersistenceCookie = JSON.parse(getCookie(REPORT_PERSISTENCE_COOKIE) || `{}`);
      if (persistenceCookie.workspace && persistenceCookie.project === projectId) {
        return persistenceCookie.workspace;
      }
    } catch (_err) {
      /* fail gracefully*/
    }
    return null;
  }

  private getGlobalWorkspaceIdForProject(projectId: number): number | null {
    const projectWorkspaces = this.getWorkspacesForProject(projectId);
    const globalWorkspace = projectWorkspaces.find((workspace) => workspace.isGlobal);
    return globalWorkspace ? globalWorkspace.id : null;
  }

  private launchAndTrackTour() {
    const {tourId, tourLink} = this.getTourInfo();

    mixpanel.track(HelpTrackingEvent.LaunchTour, {
      'tour id': tourId,
      'tour link': tourLink,
      'with chameleon': isChameleonReady(),
    });

    if (isChameleonReady()) {
      showTour(tourId);
    } else if (tourLink) {
      window.open(tourLink);
    }
  }

  private getTourInfo(): {tourId: TourId; tourLink: string | null} | null {
    return (
      {
        [Report.Insights]: {
          tourId: useNewMetricsMode(this.state.projectId).useNewMetricsMode
            ? TourId.InsightsNewMetrics
            : TourId.InsightsNewQueryBuilder,
          tourLink: `https://help.mixpanel.com/hc/en-us/articles/115004575886-Build-an-Insights-Report`,
        },
        [Report.Retention]: {
          tourId: TourId.Retention,
          tourLink: `https://help.mixpanel.com/hc/en-us/articles/360001370146-Build-a-Retention-Report`,
        },
        [Report.Impact]: {
          tourId: TourId.Impact,
          tourLink: `https://help.mixpanel.com/hc/en-us/articles/360034129112`,
        },
        [Report.Flows]: {
          tourId: TourId.Flows,
          tourLink: `https://help.mixpanel.com/hc/en-us/articles/360036438351`,
        },
      }[this.state.currentReportName] || null
    );
  }

  private async hideAllWidgets() {
    this.update({showSupportWidget: false});
    hideZendeskWidget();
    await hideCommunityWidget();
  }

  private onChameleonInitialized() {
    const welcomeTourState = getTourState(TourId.OnboardingWelcome);
    const helpAfterExitTourState = getTourState(TourId.OnboardingHelpAfterExitOnce);
    if (welcomeTourState === TourState.Exited && helpAfterExitTourState === TourState.NotStarted) {
      showTour(TourId.OnboardingHelpAfterExitOnce);
    }
  }

  private getSearchCommonProperties() {
    return {
      ...getSearchCommonTrackingProperties(),
      [SearchTrackedProperty.Source]: SearchSource.Dropdown,
      [SearchTrackedProperty.ContentDiscoveryType]: ContentDiscoveryType.Browse,
    };
  }

  private getSearchPhaseVersion() {
    return this.helpers.showNewNav() ? SearchPhaseVersion.PhaseThree : SearchPhaseVersion.PhaseTwo;
  }
}

export const ChromeHeaderEl = defineAndMakeJsxFactory(`mp-chrome-header`, ChromeHeader);
